import {
  REALTIME_SUBSCRIBE_STATES,
  type RealtimeChannel,
  type RealtimePostgresChangesPayload,
} from '@supabase/supabase-js';
import { useEffect, useRef } from 'react';
import { getSupabaseClient } from '@/shared/clients/supabaseClient';
import { isRealtimeEnabled } from '@/shared/lib/env';
import { monitoringService } from '@/shared/services/monitoringService';

/**
 * Generic Supabase realtime subscription hook (P7).
 *
 * Wraps `supabase.channel(...).on('postgres_changes', …).subscribe()` with a
 * stable lifecycle:
 *   - Subscribes on mount (and when `table` / `filter` / `enabled` change).
 *   - Cleans up (removes the channel) on unmount, so there is never a dangling
 *     websocket connection.
 *   - Keeps the latest `onChange` handler in a ref, so passing a new closure on
 *     every render does NOT tear down and re-create the subscription. Only the
 *     subscription identity (`table` + `filter` + activation) drives re-subscribe.
 *
 * Zero-connection guarantee: when realtime is not enabled (the
 * `VITE_USE_SUPABASE_REALTIME` flag is off, or the caller passes
 * `enabled: false`) the hook is a complete no-op — it never asks for a live
 * client connection and never opens a channel.
 *
 * @typeParam Row - The Postgres row shape for the subscribed table.
 *
 * @module shared/hooks/useSupabaseRealtime
 */
export interface RealtimeSubscriptionConfig<
  Row extends Record<string, unknown>,
> {
  /** Table name in the `public` schema (e.g. `'events'`). */
  readonly table: string;
  /**
   * Optional server-side row filter (e.g. `'family_id=eq.<uuid>'`). When
   * provided, Postgres only streams matching change events to this client.
   */
  readonly filter?: string;
  /**
   * Caller-level gate. When `false` the subscription is not created even if
   * realtime is globally enabled (used while async scoping ids resolve).
   * Defaults to `true`.
   */
  readonly enabled?: boolean;
  /** Invoked for every insert/update/delete on the (optionally filtered) table. */
  readonly onChange: (payload: RealtimePostgresChangesPayload<Row>) => void;
  /**
   * Invoked when the channel recovers (reaches `SUBSCRIBED`) after a
   * degraded status (`CHANNEL_ERROR` / `TIMED_OUT` / `CLOSED`). Change events
   * emitted while the channel was down were lost, so callers should refetch
   * their store state here to close the gap.
   */
  readonly onReconnect?: () => void;
}

export function useSupabaseRealtime<Row extends Record<string, unknown>>(
  config: RealtimeSubscriptionConfig<Row>
): void {
  const { table, filter, enabled = true, onChange, onReconnect } = config;

  // Keep the latest handlers without re-subscribing on every render.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onReconnectRef = useRef(onReconnect);
  onReconnectRef.current = onReconnect;

  const active = isRealtimeEnabled && enabled;

  useEffect(() => {
    if (!active) {
      return;
    }

    const channelName = `realtime:${table}:${filter ?? 'all'}`;
    const client = getSupabaseClient();

    const changeFilter = {
      event: '*' as const,
      schema: 'public',
      table,
      ...(filter != null && { filter }),
    };

    // Tracks whether this subscription has degraded since it last reached
    // SUBSCRIBED, so recovery can trigger a catch-up refetch exactly once.
    let hadFailure = false;
    // Intentional teardown (unmount / re-subscribe) emits CLOSED — not a
    // degradation, so statuses after disposal are ignored.
    let disposed = false;

    const channel: RealtimeChannel = client
      .channel(channelName)
      .on<Row>(
        // The string literal matches REALTIME_LISTEN_TYPES.POSTGRES_CHANGES.
        'postgres_changes',
        changeFilter,
        (payload) => {
          onChangeRef.current(payload);
        }
      )
      .subscribe((status, err) => {
        if (disposed) {
          return;
        }
        if (
          status === REALTIME_SUBSCRIBE_STATES.CHANNEL_ERROR ||
          status === REALTIME_SUBSCRIBE_STATES.TIMED_OUT ||
          status === REALTIME_SUBSCRIBE_STATES.CLOSED
        ) {
          hadFailure = true;
          monitoringService.trackEvent('realtime.channel_degraded', {
            table,
            filter: filter ?? null,
            status,
            error: err?.message ?? null,
          });
          return;
        }
        if (status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED && hadFailure) {
          hadFailure = false;
          monitoringService.trackEvent('realtime.channel_recovered', {
            table,
            filter: filter ?? null,
          });
          onReconnectRef.current?.();
        }
      });

    return () => {
      disposed = true;
      // removeChannel both unsubscribes and tears down the websocket entry.
      void client.removeChannel(channel);
    };
  }, [active, table, filter]);
}
